import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';
import { sniffBlobContentType } from '@/lib/mediaMime';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 根据当前页面 URL 路径动态推断应用 base path。
 * - /aigc/  → '/aigc'  (生产环境，Nginx /aigc/ 前缀)
 * - /staging/ → '/staging' (Staging 预览环境，Nginx /staging/ 前缀)
 * - 其他 → '' (本地开发，根路径)
 */
export function getBasePath(): string {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  if (path.startsWith('/aigc/') || path === '/aigc') return '/aigc';
  if (path.startsWith('/staging/') || path === '/staging') return '/staging';
  return '';
}

/**
 * 缩略图 URL（2026-09-08 缩略图体系）：asset:// 或站内 serve URL → thumb=1 版本。
 * 视频=TOS 首帧快照（f_jpg）、图片=TOS 等比缩放；serve 路由 302→TOS 预签名直出（process 参与签名，勿走 CDN——回源丢查询串）。
 * serve URL 三种形态都支持：assetId= / key=（服务端按扩展名推断类型）/ 相对或 http(s) 绝对（2026-09-08 画布 key 形态漏判=封面失效全量下载事故）。
 * 返回 '' 表示该 URL 无缩略图（外链/blob/data），调用方应回退原资源。
 */
export function resolveThumbUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return '';
  // WEBM 透明视频（2026-09-15 MediaKit 抠图产物，勿回归）：TOS 截帧服务端解码不解析 VP8
  // BlockAdditional alpha → 封面 JPG=抠像前合成画面（垫底透出=「抠图叠原画面」事故根因），
  // 且 JPG 无 alpha——WEBM 一律不出缩略图（'' → 调用方跳过封面层，<video> 直播原文件透明正常）。
  if (url.split('?')[0].toLowerCase().endsWith('.webm')) return '';
  if (url.startsWith('asset://')) {
    const id = url.replace('asset://', '');
    if (!id) return '';
    return `${getApiBase()}/assets/serve?assetId=${encodeURIComponent(id)}&thumb=1`;
  }
  // 环境前缀归一（2026-09-08 跨环境事故，勿回归）：工作流数据硬编码生产前缀 /aigc/api/...，
  // staging 上原样请求会打到生产后端——生产无 thumb 能力时 thumb=1 被忽略、返回原始完整文件
  // （实测 25MB 图×N=292MB 初始加载事故根因）；必须重写为当前环境前缀。
  const normalized = normalizeAssetUrl(url);
  if (
    !normalized.includes('thumb=')
    && /^(?:https?:\/\/[^/]+)?\/(?:aigc|staging)?\/?api\/assets\/serve\?/.test(normalized)
    && (normalized.includes('assetId=') || normalized.includes('key='))
  ) {
    return `${normalized}&thumb=1`;
  }
  return '';
}

/**
 * 返回当前环境对应的 API 基础路径。
 * 生产: /aigc/api  |  Staging: /staging/api  |  本地: /api
 */
export function getApiBase(): string {
  return getBasePath() + '/api';
}

/**
 * 将 asset:// URL 转换为可被浏览器加载的 serve URL。
 * asset:// 协议是火山引擎 Ark 平台的素材引用协议，浏览器无法直接加载。
 * 通过 /api/assets/serve?assetId=xxx 路由从 DB 查找 storage_key，
 * 生成 TOS 预签名 URL 并 302 重定向，无需 JWT 认证，<img>/<video> 标签可直接加载。
 *
 * 对于 http/https/data/blob URL，直接返回原值。
 * 对于已有的 serve URL（/aigc/api/... 或 /staging/api/... 或 /api/...），直接返回原值。
 */
export function resolveDisplayUrl(url: string | undefined | null): string {
  if (!url) return '';
  if (url.startsWith('asset://')) {
    const assetId = url.replace('asset://', '');
    return `${getApiBase()}/assets/serve?assetId=${encodeURIComponent(assetId)}`;
  }
  // 站内路径经 normalizeAssetUrl 归一到当前环境前缀（/aigc|staging 硬编码前缀防串环境）；
  // http(s)/data/blob 外链原样返回（normalizeAssetUrl 内部已放行）
  return normalizeAssetUrl(url);
}

/**
 * 规范化站内资产 URL 的环境前缀。
 * 数据库中部分资产 URL 硬编码了生产前缀（/aigc/api/assets/serve?...），
 * 在 Staging（/staging）或本地开发（''）环境下会导致请求打到错误环境。
 * 统一将以 /aigc/api/、/staging/api/ 开头的站内路径重写为当前环境的 getApiBase()。
 * 外部 URL（http/https）原样返回。
 */
export function normalizeAssetUrl(url: string | undefined | null): string {
  if (!url) return '';
  if (/^(https?:|data:|blob:|asset:)/.test(url)) return url;
  const apiBase = getApiBase();
  // 站内绝对路径（含环境前缀）：/aigc/api/...、/staging/api/...、/api/...
  const m = url.match(/^\/(?:aigc|staging)?\/?(api\/.*)$/);
  if (m) return `${apiBase.replace(/\/api$/, '')}/${m[1]}`;
  return url;
}

/**
 * 构造上传链路的请求头：认证 token + 工作组空间上下文（X-Workgroup-Id）。
 * 与 api.ts getHeaders() 的空间头逻辑保持一致。
 */
function buildUploadAuthHeaders(withContentType = true): Record<string, string> {
  const headers: Record<string, string> = {};
  if (withContentType) headers['Content-Type'] = 'application/json';
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('aigc-jwt');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const wgId = localStorage.getItem('aigc-workgroup-id');
    if (wgId && wgId !== 'null' && wgId !== '0') {
      headers['X-Workgroup-Id'] = wgId;
    }
  }
  return headers;
}

/**
 * 统一下载入口（2026-08-27，勿回归）
 *
 * 红线：**禁止 fetch+blob+createObjectURL 下载媒体**——
 * ① 整文件缓冲进内存，浏览器下载条要全部下完才出现（大视频体验差）；
 * ② serve URL 在 CDN 模式（CDN_ENABLED=true）下 302 到 cdn 域名，fetch 跟随跨域重定向
 *    无 CORS 头直接 TypeError（生产工具条下载"一直失败"的根因）。
 *
 * 正确做法：隐藏 <a> 浏览器导航到**同源流式端点**（Content-Disposition: attachment），
 * 浏览器原生进度、立即开始、零内存占用。导航不受 CORS 限制。
 *
 * URL 形态归一：
 * - asset://xxx        → serve?assetId=xxx&download=true（下载模式 302 直连 TOS 提速，2026-09-14）
 * - 站内 serve URL     → 补 download=true&filename（同源）
 * - CDN/TOS URL 且路径 /aigc-canvas/ 前缀 → serve?key=...&download=true（服务端重签，免疫签名 12h 过期）
 * - 其他 volces.com URL → /assets/download 代理（SSR白名单）
 * - 其他外链           → window.open 兜底（无法代理，交给浏览器）
 */
export function downloadAsset(rawUrl: string | undefined | null, filename?: string): void {
  if (!rawUrl || typeof window === 'undefined') return;
  const name = (filename || '').trim() || 'download';
  const apiBase = getApiBase();
  const withDownloadParams = (u: string): string => {
    let out = u;
    if (!/[?&]download=/.test(out)) out += (out.includes('?') ? '&' : '?') + 'download=true';
    if (!/[?&]filename=/.test(out)) out += `&filename=${encodeURIComponent(name)}`;
    return out;
  };

  let finalUrl: string | null = null;
  let openBlank = false;

  if (rawUrl.startsWith('asset://')) {
    const assetId = rawUrl.replace('asset://', '');
    finalUrl = withDownloadParams(`${apiBase}/assets/serve?assetId=${encodeURIComponent(assetId)}`);
  } else if (/^https?:\/\//i.test(rawUrl)) {
    try {
      const u = new URL(rawUrl);
      if (u.origin === window.location.origin) {
        // 同源绝对 URL → 按相对路径处理
        finalUrl = withDownloadParams(u.pathname + u.search);
      } else if (u.pathname.startsWith('/aigc-canvas/')) {
        // CDN / TOS 直链且 key 在站内前缀 → serve 重签流式下载
        finalUrl = withDownloadParams(`${apiBase}/assets/serve?key=${encodeURIComponent(u.pathname.replace(/^\//, ''))}`);
      } else if (u.hostname.endsWith('volces.com')) {
        finalUrl = `${apiBase}/assets/download?url=${encodeURIComponent(rawUrl)}&filename=${encodeURIComponent(name)}`;
      } else {
        openBlank = true;
      }
    } catch {
      openBlank = true;
    }
  } else if (rawUrl.startsWith('/')) {
    finalUrl = rawUrl.includes('/assets/serve') ? withDownloadParams(rawUrl) : rawUrl;
  } else {
    openBlank = true;
  }

  if (openBlank || !finalUrl) {
    window.open(rawUrl, '_blank');
    return;
  }
  // 同源导航下载：Content-Disposition: attachment 驱动，download 属性作文件名兜底
  const link = document.createElement('a');
  link.href = finalUrl;
  link.download = name;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * 规范化媒体 URL 为去重 key（同一媒体可能有多种 URL 形式）：
 * - asset://asset-xxx → asset:asset-xxx
 * - serve?assetId=xxx → asset:asset-xxx
 * - serve?key=a/b/c.png → tos:a/b/c.png
 * - TOS/CDN https URL → tos:a/b/c.png（取 pathname，忽略签名 query 与域名差异）
 * - 其他（如文本内容）→ 原字符串
 */
export function normalizeMediaKey(url: string | undefined | null): string {
  if (!url) return '';
  if (url.startsWith('asset://')) return `asset:${url.slice('asset://'.length)}`;
  // 非 URL 形态（如文本内容、data:URI 过长内容）直接返回原文
  if (!/^(https?:)?\/\//.test(url) && !url.startsWith('/')) return url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const assetId = u.searchParams.get('assetId');
    if (assetId) return `asset:${assetId}`;
    const key = u.searchParams.get('key');
    if (key) return `tos:${key.replace(/^\//, '')}`;
    return `tos:${u.pathname.replace(/^\//, '')}`;
  } catch {
    return url;
  }
}

// ==================== TOS 前端直传工具 ====================

export interface DirectUploadOptions {
  /** 要上传的文件 */
  file: File;
  /** 资产类型，默认根据文件类型推断 */
  assetType?: 'Image' | 'Video' | 'Audio';
  /** 指定素材组ID（真人人像认证后） */
  groupId?: string;
  /** 工作流ID */
  workflowId?: number | null;
  /** 工作组ID */
  workgroupId?: number | null;
  /** 上传进度回调（0-1） */
  onProgress?: (percent: number) => void;
}

export interface DirectUploadResult {
  url: string;
  key: string;
  bucket: string;
  type: string;
  assetUrl?: string;
  originalName: string;
}

/**
 * 推断文件的资产类型
 */
export function inferAssetType(file: File): 'Image' | 'Video' | 'Audio' {
  if (file.type.startsWith('image/')) return 'Image';
  if (file.type.startsWith('video/')) return 'Video';
  if (file.type.startsWith('audio/')) return 'Audio';
  // Fallback by extension
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext)) return 'Video';
  if (['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'].includes(ext)) return 'Audio';
  return 'Image';
}

/**
 * 生成可区分的默认工作流名称（带时间戳，避免一堆同名"未命名工作流"）
 * 例：未命名 08-09 19:02
 */
export function defaultWorkflowName(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `未命名 ${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 直传文件到 TOS（跳过服务器中转）
 *
 * 两步流程：
 * 1. POST /api/assets/presign → 获取预签名 PUT URL（服务器生成，不接触文件内容）
 * 2. PUT file → TOS endpoint（浏览器直传，支持进度回调）
 * 3. POST /api/assets/confirm → 确认上传，注册素材到数据库/CreateAsset
 *
 * 相比 FormData 中转方案：
 * - 服务器不接收文件二进制，内存占用为0
 * - 上传带宽走用户→TOS（同CDN节点），速度提升3-10倍
 * - 支持 onProgress 实时进度显示
 */
export async function directUploadToTOS(options: DirectUploadOptions): Promise<DirectUploadResult> {
  const { file, groupId, workflowId, workgroupId, onProgress } = options;
  const assetType = options.assetType || inferAssetType(file);
  const contentType = await sniffBlobContentType(file, file.type);

  // 自动注册到全局上传进度 store（悬浮进度窗）
  let globalTaskId: string | null = null;
  try {
    // 动态导入避免服务端渲染问题
    const { useUploadStore } = await import('@/store/uploadStore');
    globalTaskId = useUploadStore.getState().addTask(file.name, file.size);
  } catch { /* SSR 环境下忽略 */ }
  const reportProgress = (p: number, loaded?: number) => {
    onProgress?.(p);
    if (globalTaskId) {
      import('@/store/uploadStore').then(({ useUploadStore }) => {
        useUploadStore.getState().updateProgress(globalTaskId!, p, loaded);
      }).catch(() => {});
    }
  };

  // Build auth + workspace headers
  const authHeaders = buildUploadAuthHeaders();

  // Step 1: 获取预签名 URL
  const presignRes = await fetch(`${getApiBase()}/assets/presign`, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify({
      fileName: file.name,
      contentType,
      fileSize: file.size,
      assetType,
      groupId: groupId || null,
      workflowId: workflowId || null,
    }),
  });

  if (!presignRes.ok) {
    const err = await presignRes.json().catch(() => ({ error: `HTTP ${presignRes.status}` }));
    throw new Error(err.error || '获取上传URL失败');
  }

  const presignData = await presignRes.json();
  if (!presignData.success) {
    throw new Error(presignData.error || '获取上传URL失败');
  }

  const { uploadUrl, objectKey } = presignData.data;

  // Step 2: 直传文件到 TOS（使用 XMLHttpRequest 支持 progress 事件）
  try {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          reportProgress(e.loaded / e.total, e.loaded);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          reportProgress(1, file.size);
          resolve();
        } else {
          reject(new Error(`上传失败 (HTTP ${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('网络错误，上传失败'));
      xhr.ontimeout = () => reject(new Error('上传超时'));
      xhr.send(file);
    });
  } catch (err) {
    // 标记全局任务失败（上传阶段）
    if (globalTaskId) {
      import('@/store/uploadStore').then(({ useUploadStore }) => {
        useUploadStore.getState().failTask(globalTaskId!, err instanceof Error ? err.message : '上传失败');
      }).catch(() => {});
    }
    throw err;
  }

  // Step 3: 确认上传（注册到数据库 + CreateAsset）
  const confirmRes = await fetch(`${getApiBase()}/assets/confirm`, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify({
      objectKey,
      fileName: file.name,
      assetType,
      contentType,
      groupId: groupId || null,
      workflowId: workflowId || null,
      workgroupId: workgroupId || null,
    }),
  });

  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({ error: `HTTP ${confirmRes.status}` }));
    throw new Error(err.error || '确认上传失败');
  }

  const confirmData = await confirmRes.json();
  if (!confirmData.success) {
    throw new Error(confirmData.error || '确认上传失败');
  }

  // 标记全局任务完成
  if (globalTaskId) {
    import('@/store/uploadStore').then(({ useUploadStore }) => {
      useUploadStore.getState().completeTask(globalTaskId!);
    }).catch(() => {});
  }

  return {
    url: confirmData.data.url,
    key: confirmData.data.key,
    bucket: confirmData.data.bucket,
    type: confirmData.data.type,
    assetUrl: confirmData.data.assetUrl,
    originalName: confirmData.data.originalName,
  };
}

/**
 * Fallback: upload via server proxy (original tos-upload endpoint)
 * Used when direct upload fails (CORS not ready, network issues, etc.)
 */
async function fallbackUploadToServer(options: DirectUploadOptions): Promise<DirectUploadResult> {
  const { file, groupId, workflowId, workgroupId } = options;
  const assetType = options.assetType || inferAssetType(file);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('assetType', assetType);
  if (groupId) formData.append('groupId', groupId);
  if (workflowId) formData.append('workflowId', String(workflowId));

  const resp = await fetch(`${getApiBase()}/assets/tos-upload`, {
    method: 'POST',
    headers: buildUploadAuthHeaders(false),
    body: formData,
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
    throw new Error(err.error || '上传失败');
  }

  const data = await resp.json();
  if (!data.success) throw new Error(data.error || '上传失败');

  return {
    url: data.data.url,
    key: data.data.key || '',
    bucket: data.data.bucket || '',
    type: data.data.type || assetType.toLowerCase(),
    assetUrl: data.data.assetUrl,
    originalName: file.name,
  };
}

/**
 * Upload file to TOS with automatic fallback:
 * 1. Try direct-to-TOS upload (fast, no server proxy)
 * 2. If direct upload fails (CORS, network), fall back to server proxy upload
 */
export async function uploadToTOS(options: DirectUploadOptions): Promise<DirectUploadResult> {
  // 大文件（≥20MB）走分片上传（断点续传 + 并发分片）
  if (options.file.size >= MULTIPART_THRESHOLD) {
    try {
      return await multipartUploadToTOS(options);
    } catch (err) {
      console.warn('[Upload] Multipart upload failed, falling back to single PUT:', err instanceof Error ? err.message : err);
    }
  }
  try {
    return await directUploadToTOS(options);
  } catch (err) {
    // If direct upload fails, try server proxy as fallback
    console.warn('[Upload] Direct upload failed, falling back to server proxy:', err instanceof Error ? err.message : err);
    return await fallbackUploadToServer(options);
  }
}

// ─── 分片上传（Multipart Upload） ───

const MULTIPART_THRESHOLD = 20 * 1024 * 1024; // ≥20MB 启用分片
const PART_SIZE = 8 * 1024 * 1024; // 8MB/片
const PART_CONCURRENCY = 3; // 并发分片数
const CHECKPOINT_PREFIX = 'aigc-multipart:';
const CHECKPOINT_TTL = 24 * 60 * 60 * 1000; // 断点记录 24h 有效

interface MultipartCheckpoint {
  uploadId: string;
  objectKey: string;
  bucket: string;
  parts: Array<{ partNumber: number; etag: string }>;
  fileName: string;
  fileSize: number;
  createdAt: number;
}

function getFileFingerprint(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function loadCheckpoint(fingerprint: string): MultipartCheckpoint | null {
  try {
    const raw = localStorage.getItem(CHECKPOINT_PREFIX + fingerprint);
    if (!raw) return null;
    const cp = JSON.parse(raw) as MultipartCheckpoint;
    if (Date.now() - cp.createdAt > CHECKPOINT_TTL) {
      localStorage.removeItem(CHECKPOINT_PREFIX + fingerprint);
      return null;
    }
    return cp;
  } catch {
    return null;
  }
}

function saveCheckpoint(fingerprint: string, cp: MultipartCheckpoint): void {
  try {
    localStorage.setItem(CHECKPOINT_PREFIX + fingerprint, JSON.stringify(cp));
  } catch { /* 存储满时忽略，不影响上传 */ }
}

function clearCheckpoint(fingerprint: string): void {
  try {
    localStorage.removeItem(CHECKPOINT_PREFIX + fingerprint);
  } catch { /* ignore */ }
}

/** 分片上传单个分片（PUT 到预签名 URL），返回 ETag */
async function uploadPart(partUrl: string, blob: Blob): Promise<string> {
  const resp = await fetch(partUrl, { method: 'PUT', body: blob });
  if (!resp.ok) {
    throw new Error(`分片上传失败 (HTTP ${resp.status})`);
  }
  const etag = resp.headers.get('ETag') || resp.headers.get('etag');
  if (!etag) {
    throw new Error('分片响应缺少 ETag（TOS CORS 需 ExposeHeaders 包含 ETag）');
  }
  return etag.replace(/"/g, '');
}

/**
 * 分片上传大文件到 TOS（支持断点续传）
 *
 * 流程：
 * 1. 计算文件指纹，查 localStorage 断点记录 → 有则复用 uploadId 并跳过已传分片
 * 2. 无则调 /api/assets/multipart?action=create 创建任务
 * 3. 并发上传分片（每片先调 action=sign 获取预签名 URL）
 * 4. 全部完成调 action=complete 合并
 * 5. 复用 /api/assets/confirm 注册素材
 */
export async function multipartUploadToTOS(options: DirectUploadOptions): Promise<DirectUploadResult> {
  const { file, groupId, workflowId, workgroupId, onProgress } = options;
  const assetType = options.assetType || inferAssetType(file);
  const contentType = await sniffBlobContentType(file, file.type);
  const fingerprint = getFileFingerprint(file);

  const authHeaders = buildUploadAuthHeaders();

  // 全局进度注册
  let globalTaskId: string | null = null;
  try {
    const { useUploadStore } = await import('@/store/uploadStore');
    globalTaskId = useUploadStore.getState().addTask(file.name, file.size);
  } catch { /* SSR 忽略 */ }

  const totalParts = Math.ceil(file.size / PART_SIZE);
  const partLoaded = new Array<number>(totalParts).fill(0);
  const reportProgress = () => {
    const loaded = partLoaded.reduce((a, b) => a + b, 0);
    const p = Math.min(loaded / file.size, 1);
    onProgress?.(p);
    if (globalTaskId) {
      import('@/store/uploadStore').then(({ useUploadStore }) => {
        useUploadStore.getState().updateProgress(globalTaskId!, p, loaded);
      }).catch(() => {});
    }
  };

  const multipartApi = async (body: Record<string, unknown>) => {
    const resp = await fetch(`${getApiBase()}/assets/multipart`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({ success: false, error: `HTTP ${resp.status}` }));
    if (!resp.ok || !data.success) {
      throw new Error(data.error || `分片接口失败 (HTTP ${resp.status})`);
    }
    return data.data;
  };

  try {
    // Step 1: 恢复断点 or 创建任务
    let checkpoint = loadCheckpoint(fingerprint);
    if (!checkpoint) {
      const created = await multipartApi({
        action: 'create',
        fileName: file.name,
        contentType,
        fileSize: file.size,
        assetType,
      });
      checkpoint = {
        uploadId: created.uploadId,
        objectKey: created.objectKey,
        bucket: created.bucket,
        parts: [],
        fileName: file.name,
        fileSize: file.size,
        createdAt: Date.now(),
      };
      saveCheckpoint(fingerprint, checkpoint);
    }

    const { uploadId, objectKey } = checkpoint;
    const uploadedParts = new Map(checkpoint.parts.map((p) => [p.partNumber, p.etag]));

    // 已传分片直接计入进度
    for (const [pn] of uploadedParts) {
      const start = (pn - 1) * PART_SIZE;
      partLoaded[pn - 1] = Math.min(PART_SIZE, file.size - start);
    }
    reportProgress();

    // Step 2: 并发上传缺失分片
    const pendingParts: number[] = [];
    for (let i = 1; i <= totalParts; i++) {
      if (!uploadedParts.has(i)) pendingParts.push(i);
    }

    let cursor = 0;
    const worker = async () => {
      while (cursor < pendingParts.length) {
        const partNumber = pendingParts[cursor++];
        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const blob = file.slice(start, end);

        // 获取分片预签名 URL
        const { partUrl } = await multipartApi({ action: 'sign', objectKey, uploadId, partNumber });

        // 上传分片（带进度的 XHR 不如 fetch 流式方便，分片粒度 8MB 用 fetch + 完成后更新进度即可）
        const etag = await uploadPart(partUrl, blob);

        partLoaded[partNumber - 1] = end - start;
        uploadedParts.set(partNumber, etag);
        reportProgress();

        // 持久化断点
        checkpoint.parts = Array.from(uploadedParts.entries()).map(([pn, et]) => ({ partNumber: pn, etag: et }));
        saveCheckpoint(fingerprint, checkpoint);
      }
    };
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, pendingParts.length || 1) }, worker));

    // Step 3: 合并分片
    await multipartApi({
      action: 'complete',
      objectKey,
      uploadId,
      parts: Array.from(uploadedParts.entries()).map(([partNumber, etag]) => ({ partNumber, etag })),
    });

    // Step 4: 注册素材（复用 confirm 接口）
    const confirmRes = await fetch(`${getApiBase()}/assets/confirm`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        objectKey,
        fileName: file.name,
        assetType,
        contentType,
        groupId: groupId || null,
        workflowId: workflowId || null,
        workgroupId: workgroupId || null,
      }),
    });
    const confirmData = await confirmRes.json().catch(() => ({ success: false, error: `HTTP ${confirmRes.status}` }));
    if (!confirmRes.ok || !confirmData.success) {
      throw new Error(confirmData.error || '确认上传失败');
    }

    clearCheckpoint(fingerprint);
    reportProgress();
    if (globalTaskId) {
      import('@/store/uploadStore').then(({ useUploadStore }) => {
        useUploadStore.getState().completeTask(globalTaskId!);
      }).catch(() => {});
    }

    return {
      url: confirmData.data.url,
      key: confirmData.data.key,
      bucket: confirmData.data.bucket,
      type: confirmData.data.type,
      assetUrl: confirmData.data.assetUrl,
      originalName: confirmData.data.originalName,
    };
  } catch (err) {
    if (globalTaskId) {
      import('@/store/uploadStore').then(({ useUploadStore }) => {
        useUploadStore.getState().failTask(globalTaskId!, err instanceof Error ? err.message : '上传失败');
      }).catch(() => {});
    }
    throw err;
  }
}

/**
 * 批量并行直传文件到 TOS
 */
export async function directUploadBatchToTOS(
  files: File[],
  options: Omit<DirectUploadOptions, 'file'> & { onFileProgress?: (index: number, percent: number) => void } = {}
): Promise<DirectUploadResult[]> {
  const results: DirectUploadResult[] = [];
  // Limit concurrency to 3 to avoid browser connection limits
  const concurrency = 3;
  let index = 0;

  async function worker() {
    while (index < files.length) {
      const i = index++;
      const file = files[i];
      const result = await uploadToTOS({
        ...options,
        file,
        onProgress: options.onFileProgress ? (p) => options.onFileProgress!(i, p) : undefined,
      });
      results[i] = result;
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, files.length) }, worker);
  await Promise.all(workers);
  return results;
}

/* ───────────────────────────────────────────────────────────────────────────
 * 批量下载 · 浏览器端流式 ZIP 打包（2026-10-14c 定稿）
 *
 * 用户实测两痛点：①逐文件原生下载仍会被浏览器拦截（遗漏）；②文件名变成上游 ID
 * （asset:// 跨域 <a download> 失效）。综合「打包成单文件」与「直接 TOS
 * 下载」两案优点：
 * - 打包发生在**浏览器端**（zip.js 流式，逐文件边下边打边写盘）——服务器只解析
 *   URL（direct-urls 毫秒级）+ 对 CORS 不可控的 asset:// 做单文件流式管道（proxy=1
 *   强制代理，不聚合不落盘），无「全量下载完再回传」的双倍等待。
 * - 站内产物（aigc-canvas/ key）仍 TOS 预签名**直连**（mode=direct，不经服务器）。
 * - 单个 ZIP：一次选保存位置（showSaveFilePicker）、任何浏览器可用（Safari 降级
 *   Blob 一次性保存）、零遗漏（失败文件写入 zip 内「_下载失败清单.txt」）。
 * - zip 条目名 = 卡片名（+响应 Content-Type 推断的扩展名）——名字永远正确。
 * ─────────────────────────────────────────────────────────────────────────── */

const CT_EXT: Record<string, string> = {
  'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm',
  'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/mpeg': 'mp3', 'audio/mp4': 'm4a',
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif',
  'application/pdf': 'pdf', 'application/zip': 'zip',
};

function extFromResponse(resp: Response): string {
  const ct = (resp.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
  if (CT_EXT[ct]) return CT_EXT[ct];
  return '';
}

/** 从 fetch URL 路径推断扩展名（无则空串） */
function extFromUrlPath(url: string): string {
  try {
    const path = new URL(url, 'https://x.local').pathname;
    const m = path.match(/\.([a-zA-Z0-9]{2,5})$/);
    return m ? m[1].toLowerCase() : '';
  } catch {
    return '';
  }
}

interface ZipSaveHandle {
  writable: WritableStream<Uint8Array>;
  close: () => Promise<void>;
}

/* ── StreamSaver 自托管客户端（Mac Safari/Firefox 流式写盘，2026-10-16 需求 3）──
 * 无 showSaveFilePicker 的浏览器：隐藏 iframe 加载 public/streamsaver/mitm.html，
 * SW ready 后把 TransformStream 的 readable 端（transferable）转交 SW，iframe 导航
 * token URL 由 SW 以 attachment 流式响应 → 浏览器边下边写盘，不走全内存 Blob。 */

let ssIframePromise: Promise<HTMLIFrameElement> | null = null;

function ensureStreamSaverIframe(): Promise<HTMLIFrameElement> {
  if (ssIframePromise) return ssIframePromise;
  ssIframePromise = new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${getBasePath()}/streamsaver/mitm.html`;
    const timer = setTimeout(() => {
      window.removeEventListener('message', onMsg);
      reject(new Error('streamsaver 初始化超时'));
    }, 8000);
    const onMsg = (evt: MessageEvent) => {
      if (evt.source !== iframe.contentWindow) return;
      if (evt.data === 'streamsaver-ready') {
        clearTimeout(timer);
        window.removeEventListener('message', onMsg);
        resolve(iframe);
      } else if (evt.data === 'streamsaver-failed') {
        clearTimeout(timer);
        window.removeEventListener('message', onMsg);
        reject(new Error('streamsaver SW 注册失败'));
      }
    };
    window.addEventListener('message', onMsg);
    document.body.appendChild(iframe);
  });
  // 失败后允许下次重试（不缓存 rejected promise）
  ssIframePromise.catch(() => { ssIframePromise = null; });
  return ssIframePromise;
}

/** StreamSaver 流式保存目标：成功返回 writable 直接喂 zip.js；不可用返回 null（调用方降级 Blob） */
async function openStreamSaverTarget(suggestedName: string): Promise<ZipSaveHandle | null> {
  try {
    if (!('serviceWorker' in navigator) || typeof TransformStream === 'undefined') return null;
    const iframe = await ensureStreamSaverIframe();
    const ts = new TransformStream<Uint8Array, Uint8Array>();
    const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const url = `${getBasePath()}/streamsaver/download/${token}`;
    // readable 端所有权转移给 iframe（再转 SW）；此后本侧只持有 writable
    iframe.contentWindow!.postMessage(
      { type: 'streamsaver-download', url, filename: suggestedName, readableStream: ts.readable },
      '*',
      [ts.readable],
    );
    // close 无需额外动作：zip.js writer.close() 会关闭 writable，流结束即下载完成
    return { writable: ts.writable, close: async () => {} };
  } catch (e) {
    console.warn('[BatchDL] StreamSaver 不可用，降级内存 Blob:', e instanceof Error ? e.message : e);
    return null;
  }
}

/** 选一个 zip 保存目标：Chromium showSaveFilePicker → StreamSaver（Safari/Firefox 流式）→ 内存 Blob */
async function openZipTarget(suggestedName: string): Promise<ZipSaveHandle | { blob: true }> {
  const picker = (window as unknown as { showSaveFilePicker?: (opts?: unknown) => Promise<{
    createWritable: () => Promise<WritableStream<Uint8Array>>;
  }> }).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker.call(window, {
        suggestedName,
        types: [{ description: 'ZIP 压缩包', accept: { 'application/zip': ['.zip'] } }],
      });
      const writable = await handle.createWritable();
      return { writable, close: () => writable.close() };
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e; // 用户取消
      // picker 异常不直接降 Blob，继续尝试 StreamSaver 流式
    }
  }
  const ss = await openStreamSaverTarget(suggestedName);
  if (ss) return ss;
  return { blob: true };
}

export async function batchDownloadFiles(items: Array<{ url: string; name: string }>): Promise<void> {
  const list = items.filter((i) => i.url);
  if (list.length === 0) return;
  const toastId = toast.loading(`正在准备 ${list.length} 个文件…`, { duration: Infinity });

  try {
    // 1) 解析直链/代理 URL（服务器零文件传输）
    const token = localStorage.getItem('aigc-jwt') || '';
    const resp = await fetch(`${getApiBase()}/assets/direct-urls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ files: list.map((f) => ({ name: f.name, url: f.url })) }),
    });
    const data = await resp.json();
    if (!resp.ok || !data.success) throw new Error(data.error || '解析下载链接失败');
    const files = (data.files || []) as Array<{ name: string; url: string; fallback?: string }>;
    const skipped: string[] = data.skipped || [];

    // 2) 打开 zip 保存目标（一次选位置）
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
    const zipName = `批量导出_${stamp}.zip`;
    const target = await openZipTarget(zipName);

    // 3) zip.js 流式打包：逐文件 fetch → add 进 zip（store 不压缩，视频零 CPU 损耗）
    const zip = await import('@zip.js/zip.js');
    zip.configure({ useWebWorkers: false });
    let writer: import('@zip.js/zip.js').ZipWriter<WritableStream<Uint8Array>>;
    let isBlob = false;
    let finish: () => Promise<unknown>;
    if ('writable' in target) {
      writer = new zip.ZipWriter(target.writable, { level: 0 }) as import('@zip.js/zip.js').ZipWriter<WritableStream<Uint8Array>>;
      finish = () => writer.close();
    } else {
      isBlob = true;
      const blobWriter = new zip.ZipWriter(new zip.BlobWriter('application/zip'), { level: 0 });
      writer = blobWriter as unknown as import('@zip.js/zip.js').ZipWriter<WritableStream<Uint8Array>>;
      finish = async () => {
        const blob = await blobWriter.close();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = zipName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        return blob;
      };
    }

    const usedNames = new Set<string>();
    const failed: string[] = [];
    let done = 0;
    const total = files.length;
    /** 单文件拉取：direct 失败（CORS/直链失效）时自动用 fallback 同源代理重试一次（2026-10-16
     *  混合下载单格式失败修复——任一根因下不再整类格式全挂） */
    const fetchFile = async (f: { name: string; url: string; fallback?: string }): Promise<{ resp: Response; via: string }> => {
      const primary = f.url.startsWith('/') ? `${getApiBase()}${f.url}` : f.url;
      try {
        const r = await fetch(primary);
        if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
        return { resp: r, via: primary };
      } catch (e) {
        if (!f.fallback) throw e;
        const fb = f.fallback.startsWith('/') ? `${getApiBase()}${f.fallback}` : f.fallback;
        console.warn('[BatchDL] direct 拉取失败，改走同源代理重试:', f.name, e instanceof Error ? e.message : e);
        const r2 = await fetch(fb);
        if (!r2.ok || !r2.body) throw new Error(`HTTP ${r2.status}（代理重试）`);
        return { resp: r2, via: fb };
      }
    };
    for (const f of files) {
      toast.loading(`打包中 ${done + 1}/${total}：${f.name}`, { id: toastId });
      try {
        const { resp: r, via: fetchUrl } = await fetchFile(f);
        // 文件名 = 卡片名 + 扩展名（URL 路径 → 响应 Content-Type 兜底）
        const ext = extFromUrlPath(fetchUrl) || extFromResponse(r);
        const dot = f.name.lastIndexOf('.');
        const hasExt = dot > 0 && f.name.length - dot <= 6;
        let entryName = hasExt ? f.name : `${f.name}${ext ? '.' + ext : ''}`;
        // zip 条目重名自动加序号
        if (usedNames.has(entryName)) {
          const d2 = entryName.lastIndexOf('.');
          let i = 1;
          while (usedNames.has(entryName)) {
            entryName = d2 > 0 ? `${entryName.slice(0, d2)}(${i})${entryName.slice(d2)}` : `${entryName}(${i})`;
            i += 1;
          }
        }
        usedNames.add(entryName);
        await writer.add(entryName, r.body, { level: 0 });
        done += 1;
      } catch (e) {
        console.warn('[BatchDL] 打包失败:', f.name, e instanceof Error ? e.message : e);
        failed.push(f.name);
      }
    }

    // 4) 失败清单写入 zip（用户解压即可见，替代静默遗漏）
    if (skipped.length > 0 || failed.length > 0) {
      const lines = [
        '以下文件未能包含在本压缩包中：',
        ...(skipped.length ? ['', '[链接无法解析]', ...skipped.map((s) => `- ${s}`)] : []),
        ...(failed.length ? ['', '[下载失败]', ...failed.map((s) => `- ${s}`)] : []),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      await writer.add('_下载失败清单.txt', new Response(blob).body!);
    }
    await finish();

    if (failed.length === 0 && skipped.length === 0) {
      toast.success(`${done} 个文件已打包完成`, { id: toastId, duration: 4000 });
    } else {
      const bad = skipped.length + failed.length;
      toast.warning(`${done} 个已打包，${bad} 个未包含（详见压缩包内失败清单）`, { id: toastId, duration: 6000 });
    }
  } catch (e) {
    const msg = e instanceof DOMException && e.name === 'AbortError' ? '' : e instanceof Error ? e.message : String(e);
    if (msg) toast.error(`批量下载失败：${msg}`, { id: toastId, duration: 6000 });
    else toast.dismiss(toastId);
  }
}
